/**
 * Copy for the MSR3 "you are on the new version" banner and its return-to-MSR2
 * confirmation (APP-2283, register items 8 and 9).
 *
 * Approved wording lives here so the feature works with only the flag set. The
 * banner and dialog prose — the strings in MSR3_RETURN_BANNER_CONFIG_KEYS — can
 * be overridden from Parameter Store via `GET /setting/configuration`, so
 * Member Services can reword without a deploy. Button labels, field labels and
 * the reason list are not overridable.
 * Retired at the 22 October 2026 cutover along with the rest of this feature.
 */
import { readConfigString } from '../utils/helpers/configurationValue';

export const MSR3_RETURN_BANNER_CONFIG_KEYS = {
  actionLabel: 'msr3.returnBanner.actionLabel',
  body: 'msr3.returnBanner.body',
  confirmBody: 'msr3.returnBanner.confirm.body',
  confirmFooter: 'msr3.returnBanner.confirm.footer',
  confirmTitle: 'msr3.returnBanner.confirm.title',
  title: 'msr3.returnBanner.title',
} as const;

/**
 * Profiles and workspaces created after this stay in the new experience: they
 * only ever had MSR3, so offering a way "back" to a version they have not used
 * would confuse them. Confirmed with product 2026-08-20.
 */
const NEW_MEMBER_CUTOFF = '2026-09-24T00:00:00.000Z';

export const msr3ReturnBannerCopy = {
  banner: {
    actionLabel: 'Return to the previous version',
    body: 'You are currently using the redesigned version of MyScreenrights, which officially launches on 22 October 2026. You can return to the previous version at any time up until 21 October 2026. After this, all users will be automatically redirected to the new platform.',
    title: 'You are using the new version of MyScreenrights',
  },
  confirm: {
    // The register's wording pointed members at the chat icon. The dialog now
    // asks in place, so that sentence would send them looking for a widget
    // while the box sits in front of them. Flagged for Dom to confirm.
    body: 'You are about to navigate back to the previous version of MyScreenrights, which will remain accessible until 21 October 2026.\n\nBefore you go, could you tell us what you need from MyScreenrights that you couldn’t find or do here?',
    cancelLabel: 'Cancel',
    // Sits under the fields, next to the buttons: it is the question the
    // buttons answer, so it reads oddly above a form the member has not filled.
    footer:
      'You can switch back to this new version at any time. Do you want to continue?',
    sendingLabel: 'Sending…',
    submitLabel: 'Submit and return',
    title: 'Returning to the previous version of MyScreenrights?',
  },
  feedback: {
    reasonLabel: 'Reason for returning (optional)',
    reasonPlaceholder: 'Select a reason',
    textLabel: 'Anything else you’d like to tell us? (optional)',
  },
  // Not configurable: these are the reason values reported to analytics, and
  // rewording one mid-flight would split a single reason across two labels.
  reasons: [
    'I couldn\u2019t find something',
    'Something did not work',
    'I prefer the previous layout',
    'A feature I need is missing',
    'Just looking',
    'Other',
  ],
} as const;

export type Msr3ReturnBannerCopy = {
  actionLabel: string;
  body: string;
  confirmBody: string;
  confirmFooter: string;
  confirmTitle: string;
  title: string;
};

export const resolveMsr3ReturnBannerCopy = (
  configuration?: Record<string, unknown> | null,
): Msr3ReturnBannerCopy => {
  const read = (key: string, fallback: string) =>
    readConfigString(configuration, key) || fallback;

  return {
    actionLabel: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.actionLabel,
      msr3ReturnBannerCopy.banner.actionLabel,
    ),
    body: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.body,
      msr3ReturnBannerCopy.banner.body,
    ),
    confirmBody: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.confirmBody,
      msr3ReturnBannerCopy.confirm.body,
    ),
    confirmFooter: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.confirmFooter,
      msr3ReturnBannerCopy.confirm.footer,
    ),
    confirmTitle: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.confirmTitle,
      msr3ReturnBannerCopy.confirm.title,
    ),
    title: read(
      MSR3_RETURN_BANNER_CONFIG_KEYS.title,
      msr3ReturnBannerCopy.banner.title,
    ),
  };
};

/**
 * True for a profile or workspace created after {@link NEW_MEMBER_CUTOFF}.
 * Callers compose this per subject, so the same rule applies to the member and
 * to whichever workspaces are in scope.
 */
export const isCreatedAfterMsr3Cutoff = (
  created: string | undefined,
): boolean => {
  if (!created) return false;

  const createdAt = Date.parse(created);

  // Fails open: a missing or unreadable date shows the banner rather than
  // silently hiding an approved notice.
  if (Number.isNaN(createdAt)) return false;

  return createdAt > Date.parse(NEW_MEMBER_CUTOFF);
};
